const API_BASE ='https://credify-api.jiamsols.com/api/background-check';

const getToken = () => {
  return localStorage.getItem('authToken');
};

const handleResponse = async (response) => {
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data.message || `HTTP Error: ${response.status}`);
  }
  return data;
};

export const backgroundCheckAPI = {
    // EMPLOYER APIs
    /**
   * Get all background checks (filtered by role on backend)
   * Backend route: GET /
   * @param {Object} filters - Optional filters { status, result }
   * @returns {Promise} List of background checks
   */
  getAll: async (filters = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.result) queryParams.append('result', filters.result);

      const queryString = queryParams.toString();
      const url = `${API_BASE}${queryString ? `?${queryString}` : ''}`;

      console.log('📋 Fetching all background checks...');
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
      });

      return handleResponse(response);
    } catch (error) {
      console.error('❌ Error fetching background checks:', error);
      throw error;
    }
  },

  /**
   * Get employer's background check requests (alias for semantic clarity)
   * Backend route: GET /
   * @returns {Promise} List of employer's background check requests
   */
  getEmployerRequests: async () => {
    try {
      console.log('📋 Fetching employer requests...');
      return await backgroundCheckAPI.getAll();
    } catch (error) {
      console.error('❌ Error fetching employer requests:', error);
      throw error;
    }
  },

  /**
   * Assign background check to agency
   * Backend route: POST /assign
   * @param {Object} data - Request payload
   * @param {string} data.candidateEmail - Candidate email (optional if candidateUserId provided)
   * @param {string} data.candidateUserId - Candidate user ID (optional if email provided)
   * @param {string} data.candidateProfileId - Candidate profile ID (optional)
   * @param {string} data.agencyId - Agency ID (required)
   * @param {Array<string>} data.scope - Verification scope array (required)
   * @returns {Promise} Created background check
   */
  assignBackgroundCheck: async (data) => {
    try {
      console.log('📤 Assigning background check:', data);

      const response = await fetch(`${API_BASE}/assign`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      return handleResponse(response);
    } catch (error) {
      console.error('❌ Error assigning background check:', error);
      throw error;
    }
  },

  /**
   * Get specific background check status
   * Backend route: GET /:checkId/status
   * @param {string} checkId - Background check ID
   * @returns {Promise} Status information
   */
  getStatus: async (checkId) => {
    try {
      console.log(`📊 Fetching status for check: ${checkId}`);
      
      const response = await fetch(`${API_BASE}/${checkId}/status`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
      });

      return handleResponse(response);
    } catch (error) {
      console.error(`❌ Error fetching status for check ${checkId}:`, error);
      throw error;
    }
  },

  /**
   * Get specific background check report (full details)
   * Backend route: GET /:checkId/report
   * @param {string} checkId - Background check ID
   * @returns {Promise} Full report with details
   */
  getReport: async (checkId) => {
    try {
      console.log(`📄 Fetching report for check: ${checkId}`);
      
      const response = await fetch(`${API_BASE}/${checkId}/report`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
      });

      return handleResponse(response);
    } catch (error) {
      console.error(`❌ Error fetching report for check ${checkId}:`, error);
      throw error;
    }
  },

  /**
   * Get audit trail for a background check
   * Backend route: GET /:checkId/audit
   * @param {string} checkId - Background check ID
   * @returns {Promise} Audit log entries
   */
  getAudit: async (checkId) => {
    try {
      console.log(`📜 Fetching audit for check: ${checkId}`);
      
      const response = await fetch(`${API_BASE}/${checkId}/audit`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
      });

      return handleResponse(response);
    } catch (error) {
      console.error(`❌ Error fetching audit for check ${checkId}:`, error);
      throw error;
    }
  },

  /**
   * Get list of verified agencies
   * Backend route: GET /agencies/verified (NOT /agencies)
   * @returns {Promise} List of verified agencies with { id, email, profile }
   */
  getVerifiedAgencies: async () => {
    try {
      console.log('🔍 Fetching verified agencies...');
      
      const response = await fetch(`${API_BASE}/agencies/verified`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await handleResponse(response);
      console.log('✅ Agencies loaded:', result.data?.length || 0);
      return result;
    } catch (error) {
      console.error('❌ Error fetching verified agencies:', error);
      throw error;
    }
  },

  // AGENCY APIs
  
  /**
   * Get all assignments for logged-in agency
   * Backend route: GET /agency/assignments
   * @returns {Promise} List of assigned background checks
   */
  getAgencyAssignments: async () => {
    try {
      console.log('📋 Fetching agency assignments...');
      
      const response = await fetch(`${API_BASE}/agency/assignments`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
      });

      return handleResponse(response);
    } catch (error) {
      console.error('❌ Error fetching agency assignments:', error);
      throw error;
    }
  },

  /**
   * Backend route: PATCH /:checkId/status
   * @param {string} checkId - Background check ID
   * @param {string} status - New status (IN_PROGRESS, COMPLETED, ON_HOLD, UNABLE_TO_VERIFY)
   * @param {string} comments - Optional comments
   * @returns {Promise} Updated background check
   */
  updateStatus: async (checkId, status, comments = '') => {
    try {
      console.log(`🔄 Updating status for check ${checkId} to: ${status}`);
      
      const response = await fetch(`${API_BASE}/${checkId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status, comments }),
      });

      return handleResponse(response);
    } catch (error) {
      console.error(`❌ Error updating status for check ${checkId}:`, error);
      throw error;
    }
  },

  /**
   * Upload background report (Agency only)
   * Backend route: POST /:checkId/report
   * @param {string} checkId - Background check ID
   * @param {string} reportUrl - URL of uploaded report
   * @param {string} result - Result (PASS, FAIL, CONDITIONAL)
   * @param {string} comments - Optional comments
   * @returns {Promise} Updated background check
   */
  uploadReport: async (checkId, reportUrl, result, comments = '') => {
    try {
      console.log(`📤 Uploading report for check ${checkId}:`, { reportUrl, result });
      
      const response = await fetch(`${API_BASE}/${checkId}/report`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          reportUrl,
          result,
          comments,
        }),
      });

      return handleResponse(response);
    } catch (error) {
      console.error(`❌ Error uploading report for check ${checkId}:`, error);
      throw error;
    }
  },

  // CANDIDATE APIs

  /**
   * Get candidate's own background checks
   * Backend route: GET /candidate/list
   * @returns {Promise} List of background checks for logged-in candidate
   */
  getCandidateChecks: async () => {
    try {
      console.log('📋 Fetching candidate background checks...');
      
      const response = await fetch(`${API_BASE}/candidate/list`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
      });

      return handleResponse(response);
    } catch (error) {
      console.error('❌ Error fetching candidate background checks:', error);
      throw error;
    }
  },

  /**
   * Give consent for background check
   * Backend route: POST /:checkId/consent
   * @param {string} checkId - Background check ID
   * @param {boolean} consentGiven - Consent status (default: true)
   * @returns {Promise} Updated background check
   */
  giveConsent: async (checkId, consentGiven = true) => {
    try {
      console.log(`✅ Providing consent for check ${checkId}:`, consentGiven);
      
      const response = await fetch(`${API_BASE}/${checkId}/consent`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ consentGiven }),
      });

      return handleResponse(response);
    } catch (error) {
      console.error(`❌ Error providing consent for check ${checkId}:`, error);
      throw error;
    }
  },
};
